import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail, downloadExport } from "../lib/api";
import { PageHeader, EmptyState, Confirm } from "../components/Shared";
import { fmtFCFA, ROLES } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Pencil, Trash2, FileText, History, Send, Eye } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { TableSkeleton } from "../components/Skeletons";
import { EditableListSelect } from "../components/EditableListSelect";

const SCHEDULES = ["Monthly", "Two Weeks", "Weekly", "Undefined"];
const MONTHLY_FACTOR = { "Monthly": 1, "Two Weeks": 26 / 12, "Weekly": 52 / 12, "Undefined": 1 };
const ALLOW_MONTHLY = { "Annually": 1 / 12, "Monthly": 1, "Weekly": 52 / 12, "Daily": 365 / 12, "Undefined": 0 };
const INTERVALS = ["Annually", "Monthly", "Weekly", "Daily", "Undefined"];
const ALLOWANCE_FIELDS = [["duty_post", "Duty post"], ["telephone", "Telephone"], ["transport", "Transport"], ["food", "Food"], ["encouragement", "Encouragement"]];
const LABELS = Object.fromEntries(ALLOWANCE_FIELDS);
const emptyAllow = () => ALLOWANCE_FIELDS.reduce((o, [k]) => ({ ...o, [k]: { amount: "", interval: "Undefined", next_date: "" } }), {});
const allowArrayToMap = (arr) => {
  const m = emptyAllow();
  (arr || []).forEach((a) => { if (a.key && m[a.key]) m[a.key] = { amount: a.amount ?? "", interval: a.interval || "Undefined", next_date: (a.next_date || "").slice(0, 10) }; });
  return m;
};
const allowMapToArray = (m) => ALLOWANCE_FIELDS.map(([k]) => ({ key: k, amount: Number(m[k]?.amount) || 0, interval: m[k]?.interval || "Undefined", next_date: m[k]?.next_date || "" }));
const empty = { employee_id: "", payment_schedule: "Monthly", contract_amount: "", advance: "", salary: "", allowances: emptyAllow(), allowance_project_id: "" };
const isContractor = (cat) => (cat || "").toLowerCase().includes("contractor");
const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-sm">{label}</Label>{children}</div>;

export default function Salaries() {
  const { isRole } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [viewing, setViewing] = useState(null);
  const canManage = isRole(ROLES.CEO);

  const { data: rows = [], isLoading } = useQuery({ queryKey: ["salaries"], queryFn: () => api.get("/salaries").then((r) => r.data) });
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: () => api.get("/employees").then((r) => r.data) });
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: () => api.get("/projects").then((r) => r.data) });
  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: () => api.get("/companies").then((r) => r.data) });
  const [payrollOpen, setPayrollOpen] = useState(false);
  const [pUnit, setPUnit] = useState("all");
  const [pFrom, setPFrom] = useState("");
  const [pTo, setPTo] = useState("");
  const downloadPayroll = () => {
    const qs = new URLSearchParams();
    if (pUnit && pUnit !== "all") qs.set("company", pUnit);
    if (pFrom) qs.set("date_from", pFrom);
    if (pTo) qs.set("date_to", pTo);
    const q = qs.toString();
    downloadExport(`/export/salaries/payroll-summary.pdf${q ? `?${q}` : ""}`, "payroll_summary.pdf");
    setPayrollOpen(false);
  };
  const load = () => qc.invalidateQueries();

  const rowAmount = (s) => (isContractor(s.staff_category) ? Number(s.contract_amount) : Number(s.salary)) || 0;
  const rowAllowTotal = (s) => (s.allowances || []).reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
  const rowTotal = (s) => rowAmount(s) + rowAllowTotal(s);
  const allowMonthly = (s) => (s.allowances || []).reduce((sum, a) => sum + (Number(a.amount) || 0) * (ALLOW_MONTHLY[a.interval] ?? 0), 0);
  const monthlyTotal = rows.reduce((sum, s) => sum + rowAmount(s) * (MONTHLY_FACTOR[s.payment_schedule] ?? 1) + allowMonthly(s), 0);
  const unitRows = (() => {
    const m = {};
    rows.forEach((s) => { const u = s.unit_name || "—"; m[u] = (m[u] || 0) + rowAmount(s) * (MONTHLY_FACTOR[s.payment_schedule] ?? 1) + allowMonthly(s); });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  })();

  const openHistory = async (s) => {
    setHistoryFor(s); setHistory([]);
    try { const { data } = await api.get("/salaries/history", { params: { employee_id: s.employee_id } }); setHistory(data); }
    catch { setHistory([]); }
  };

  const raiseCheck = (s, a) => {
    const label = LABELS[a.key] || a.key;
    const ref = `${s.id}:${a.key}:${(a.next_date || "").slice(0, 10)}`;
    const qsp = new URLSearchParams({ prefill: "1", name: s.employee_name || "", amount: String(Math.round(a.amount || 0)),
      purpose: "Allowance", note: `${label} allowance`, project: s.allowance_project_id || "", aref: ref });
    navigate(`/checks?${qsp.toString()}`);
  };

  const raiseAll = async (s) => {
    try {
      const { data } = await api.post(`/salaries/${s.id}/raise-allowances`);
      if (data.created) toast.success(`Raised ${data.created} allowance check${data.created === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} already raised` : ""}`);
      else toast.info(data.skipped ? "All allowance checks are already raised." : "No allowances to raise.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const raiseUnit = async () => {
    try {
      const { data } = await api.post("/salaries/raise-unit-allowances");
      if (data.created) toast.success(`Raised ${data.created} due allowance check${data.created === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} already raised` : ""}`);
      else toast.info(data.skipped ? "All due allowance checks are already raised." : "No allowances are due in this unit right now.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const raiseAllUnits = async () => {
    try {
      const { data } = await api.post("/salaries/raise-all-units-allowances");
      if (data.created) toast.success(`Raised ${data.created} due allowance check${data.created === 1 ? "" : "s"} across all units${data.skipped ? ` · ${data.skipped} already raised` : ""}`);
      else toast.info(data.skipped ? "All due allowance checks are already raised." : "No allowances are due across any unit right now.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const selectedEmp = employees.find((e) => e.id === form.employee_id) || null;
  const contractor = isContractor(selectedEmp?.staff_category);
  const baseAmt = Number(contractor ? form.contract_amount : form.salary) || 0;
  const allowSum = ALLOWANCE_FIELDS.reduce((sum, [k]) => sum + (Number(form.allowances[k]?.amount) || 0), 0);
  const formTotal = baseAmt + allowSum;

  const openNew = () => { setEditing(null); setForm({ ...empty, allowances: emptyAllow() }); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ employee_id: s.employee_id, payment_schedule: s.payment_schedule || "Monthly", contract_amount: s.contract_amount ?? "", advance: s.advance ?? "", salary: s.salary ?? "", allowances: allowArrayToMap(s.allowances), allowance_project_id: s.allowance_project_id || "" });
    setOpen(true);
  };
  const setAllow = (k, field, val) => setForm((f) => ({ ...f, allowances: { ...f.allowances, [k]: { ...f.allowances[k], [field]: val } } }));

  const save = async () => {
    if (!form.employee_id) { toast.error("Select an employee first."); return; }
    const payload = {
      employee_id: form.employee_id,
      payment_schedule: form.payment_schedule || "Undefined",
      contract_amount: Number(form.contract_amount) || 0,
      advance: Number(form.advance) || 0,
      salary: Number(form.salary) || 0,
      allowances: allowMapToArray(form.allowances),
      allowance_project_id: form.allowance_project_id || "",
    };
    try {
      if (editing) await api.put(`/salaries/${editing.id}`, payload);
      else await api.post("/salaries", payload);
      toast.success(editing ? "Salary updated" : "Salary created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (s) => {
    try { await api.delete(`/salaries/${s.id}`); toast.success("Salary deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Salaries" subtitle="Employee pay schedules and amounts">
        <Button variant="outline" data-testid="salary-export-pdf" onClick={() => setPayrollOpen(true)}><FileText className="h-4 w-4 mr-2" /> Payroll summary</Button>
        {canManage && <Button variant="outline" data-testid="salary-raise-unit" onClick={raiseUnit}><Send className="h-4 w-4 mr-2" /> Raise due (unit)</Button>}
        {canManage && <Button variant="outline" data-testid="salary-raise-all-units" onClick={raiseAllUnits}><Send className="h-4 w-4 mr-2" /> Raise due (all units)</Button>}
        {canManage && <Button data-testid="salary-add-button" onClick={openNew} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Plus className="h-4 w-4 mr-2" /> Add Salary</Button>}
      </PageHeader>

      {rows.length > 0 && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-white shadow-sm p-4" data-testid="salary-monthly-total">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-400">Estimated monthly payroll</div>
              <div className="text-2xl font-bold text-slate-800 font-mono-num" data-testid="salary-monthly-total-value">{fmtFCFA(monthlyTotal)}</div>
            </div>
            <div className="text-xs text-slate-400 text-right">Across {rows.length} salary record{rows.length === 1 ? "" : "s"}<br />All intervals & allowances normalised to a month</div>
          </div>
          {unitRows.length > 1 && (
            <div className="mt-4 pt-3 border-t border-slate-100">
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">By business unit</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                {unitRows.map(([u, v]) => (
                  <div key={u} className="flex items-center justify-between text-sm" data-testid={`payroll-unit-${u}`}>
                    <span className="text-slate-600 truncate">{u}</span>
                    <span className="font-mono-num text-slate-800">{fmtFCFA(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? <TableSkeleton cols={4} /> : rows.length === 0 ? (
          <EmptyState text="No salaries yet." action={canManage ? <Button data-testid="salary-empty-add" onClick={openNew} className="bg-[#1E3A8A] hover:bg-[#152C6F]">Add your first salary</Button> : null} />
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Employee</TableHead><TableHead>Payment Schedule</TableHead>
              <TableHead className="text-right">Salary</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.id} data-testid={`salary-row-${s.id}`} className="group">
                  <TableCell className="font-medium text-slate-800 align-top">
                    <div>{s.employee_name || "—"}{isContractor(s.staff_category) && <span className="ml-2 text-[11px] font-semibold text-purple-700" data-testid={`salary-subcontractor-tag-${s.id}`}>Subcontractor</span>}</div>
                    {(s.allowances || []).some((a) => (a.amount || 0) > 0) && (
                      <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`salary-allowances-${s.id}`}>
                        {(s.allowances || []).filter((a) => (a.amount || 0) > 0).map((a) => (
                          <span key={a.key} data-testid={`salary-allowance-${s.id}-${a.key}`}
                            className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                            <span className="font-medium text-foreground">{LABELS[a.key] || a.key}</span>
                            <span className="font-mono-num">{fmtFCFA(a.amount)}</span>
                            <span className="opacity-70">· {a.interval}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-600">{s.payment_schedule || "—"}</TableCell>
                  <TableCell className="text-right font-mono-num" data-testid={`salary-amount-${s.id}`}>{fmtFCFA(rowTotal(s))}</TableCell>
                  <TableCell className="text-right align-top">
                    <div className="flex items-center justify-end">
                      <div data-testid={`salary-actions-${s.id}`} className="flex items-center gap-0.5 rounded-lg border border-slate-200 bg-white shadow-md px-1 py-0.5 opacity-100 transition-opacity md:focus-within:opacity-100">
                        <Button size="icon" variant="ghost" data-testid={`salary-view-${s.id}`} title="View details" onClick={() => setViewing(s)}><Eye className="h-4 w-4" /></Button>
                        {canManage && (s.allowances || []).some((a) => (a.amount || 0) > 0) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="icon" variant="ghost" data-testid={`salary-raise-${s.id}`} title="Raise allowance check"><Send className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem data-testid={`salary-raise-all-${s.id}`} onClick={() => raiseAll(s)} className="font-semibold text-[#1E3A8A]">Raise all allowances</DropdownMenuItem>
                              {(s.allowances || []).filter((a) => (a.amount || 0) > 0).map((a) => (
                                <DropdownMenuItem key={a.key} data-testid={`salary-raise-${s.id}-${a.key}`} onClick={() => raiseCheck(s, a)}>
                                  {LABELS[a.key] || a.key} · {fmtFCFA(a.amount)}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        {canManage && <Button size="icon" variant="ghost" data-testid={`salary-payslip-${s.id}`} title="Download payslip" onClick={() => downloadExport(`/export/salary/${s.id}/payslip.pdf`, `payslip_${s.employee_name}.pdf`)}><FileText className="h-4 w-4" /></Button>}
                        {canManage && <Button size="icon" variant="ghost" data-testid={`salary-history-${s.id}`} title="Salary history" onClick={() => openHistory(s)}><History className="h-4 w-4" /></Button>}
                        {canManage && <Button size="icon" variant="ghost" data-testid={`salary-edit-${s.id}`} title="Edit" onClick={() => openEdit(s)}><Pencil className="h-4 w-4" /></Button>}
                        {canManage && <Confirm onConfirm={() => remove(s)} title="Delete salary?" description={`This removes the salary record for "${s.employee_name}".`}
                          trigger={<Button size="icon" variant="ghost" data-testid={`salary-delete-${s.id}`} title="Delete" className="text-red-600"><Trash2 className="h-4 w-4" /></Button>} />}
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Salary" : "Add Salary"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <F label="Employee">
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="salary-employee-select"><SelectValue placeholder="Select an employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>

            {selectedEmp && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-1" data-testid="salary-employee-details">
                <VRow label="Profession">{selectedEmp.profession || "—"}</VRow>
                <VRow label="Designation">{selectedEmp.category || "—"}</VRow>
                <VRow label="Category">{selectedEmp.staff_category || "—"}</VRow>
                <VRow label="Gender">{selectedEmp.gender || "—"}</VRow>
                <VRow label="Phone">{selectedEmp.phone || "—"}</VRow>
                <VRow label="Email">{selectedEmp.email || "—"}</VRow>
                <VRow label="Current salary">{fmtFCFA(selectedEmp.salary)}</VRow>
              </div>
            )}

            {contractor ? (
              <>
              <F label="Contract Amount (FCFA)">
                <Input data-testid="salary-contract-amount" type="number" value={form.contract_amount} onChange={(e) => setForm({ ...form, contract_amount: e.target.value })} placeholder="0" />
              </F>
              <F label="Advance (FCFA)">
                <Input data-testid="salary-advance" type="number" value={form.advance} onChange={(e) => setForm({ ...form, advance: e.target.value })} placeholder="0" />
              </F>
              </>
            ) : (
              <F label="Salary (FCFA)">
                <Input data-testid="salary-amount" type="number" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} placeholder="0" />
              </F>
            )}

            <F label="Payment Schedule">
              <EditableListSelect value={form.payment_schedule} onValueChange={(v) => setForm({ ...form, payment_schedule: v })}
                endpoint="/payment-schedules" noun="schedule" canManage={canManage} placeholder="Select schedule" testPrefix="salary-schedule" />
            </F>

            {!contractor && (
            <div className="pt-3 border-t border-slate-200">
              <h4 className="text-sm font-semibold text-slate-700 mb-1" data-testid="allowances-title">Allowances</h4>
              <p className="text-xs text-slate-400 mb-3">The finance manager is reminded 2 days before each Monthly/Annually allowance is due, to generate a check and send it to the CEO.</p>
              <div className="mb-3">
                <Label className="text-xs text-slate-500">Debit project for allowance checks</Label>
                <Select value={form.allowance_project_id || ""} onValueChange={(v) => setForm({ ...form, allowance_project_id: v })}>
                  <SelectTrigger data-testid="allowance-project"><SelectValue placeholder="Select a project (optional)" /></SelectTrigger>
                  <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-3">
                {ALLOWANCE_FIELDS.map(([k, label]) => {
                  const a = form.allowances[k] || {};
                  const showDate = a.interval === "Annually" || a.interval === "Monthly";
                  return (
                    <div key={k} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end" data-testid={`allowance-row-${k}`}>
                      <div className="sm:col-span-4">
                        <Label className="text-xs text-slate-500">{label} (FCFA)</Label>
                        <Input data-testid={`allowance-${k}-amount`} type="number" value={a.amount} onChange={(e) => setAllow(k, "amount", e.target.value)} placeholder="0" />
                      </div>
                      <div className="sm:col-span-4">
                        <Label className="text-xs text-slate-500">Interval</Label>
                        <Select value={a.interval} onValueChange={(v) => setAllow(k, "interval", v)}>
                          <SelectTrigger data-testid={`allowance-${k}-interval`}><SelectValue /></SelectTrigger>
                          <SelectContent>{INTERVALS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="sm:col-span-4">
                        {showDate ? (
                          <>
                            <Label className="text-xs text-slate-500">Next payment date</Label>
                            <Input data-testid={`allowance-${k}-date`} type="date" value={a.next_date} onChange={(e) => setAllow(k, "next_date", e.target.value)} />
                          </>
                        ) : (
                          <p className="text-xs text-slate-400 pb-2.5">Reminders apply to Monthly / Annually only</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            )}
            {form.employee_id && (
              contractor ? (
                <div className="rounded-lg border border-purple-100 bg-purple-50/60 px-4 py-3 space-y-1.5" data-testid="salary-total-summary">
                  <div className="flex items-center justify-between text-sm"><span className="text-purple-700 font-semibold">Contract amount</span><span className="font-mono-num text-purple-700">{fmtFCFA(Number(form.contract_amount) || 0)}</span></div>
                  <div className="flex items-center justify-between text-sm"><span className="text-amber-600 font-semibold">Advance</span><span className="font-mono-num text-amber-600">{fmtFCFA(Number(form.advance) || 0)}</span></div>
                  <div className="flex items-center justify-between text-sm font-semibold border-t border-purple-100 pt-1.5"><span className="text-emerald-700">Balance</span><span className="font-mono-num text-emerald-700" data-testid="salary-balance-value">{fmtFCFA((Number(form.contract_amount) || 0) - (Number(form.advance) || 0))}</span></div>
                </div>
              ) : (
                <div className="rounded-lg border border-blue-100 bg-blue-50/60 px-4 py-3 space-y-1.5" data-testid="salary-total-summary">
                  <div className="flex items-center justify-between text-sm"><span className="text-slate-500">Salary</span><span className="font-mono-num">{fmtFCFA(baseAmt)}</span></div>
                  <div className="flex items-center justify-between text-sm"><span className="text-slate-500">+ Allowances</span><span className="font-mono-num">{fmtFCFA(allowSum)}</span></div>
                  <div className="flex items-center justify-between text-sm font-semibold border-t border-blue-100 pt-1.5"><span className="text-slate-700">Total (salary + allowances)</span><span className="font-mono-num text-[#1E3A8A]" data-testid="salary-total-value">{fmtFCFA(formTotal)}</span></div>
                </div>
              )
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="salary-form-submit" onClick={save} disabled={!form.employee_id} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">{editing ? "Save Changes" : "Add Salary"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyFor} onOpenChange={(o) => { if (!o) setHistoryFor(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="salary-history-dialog">
          <DialogHeader><DialogTitle>Salary history · {historyFor?.employee_name}</DialogTitle></DialogHeader>
          {history.length === 0 ? (
            <p className="text-sm text-slate-400 py-4" data-testid="salary-history-empty">No history recorded yet.</p>
          ) : (
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
              {history.map((h) => (
                <div key={h.id} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`salary-history-row-${h.id}`}>
                  <div>
                    <div className="font-medium text-slate-800 font-mono-num">
                      {h.kind === "allowance" && <span className="text-slate-700 font-sans">{h.label}: </span>}{fmtFCFA(h.amount)}
                    </div>
                    <div className="text-xs text-slate-400">{h.payment_schedule} · {h.action}{h.kind === "allowance" ? " · allowance" : ""}</div>
                  </div>
                  <span className="text-xs text-slate-500">{new Date(h.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryFor(null)} data-testid="salary-history-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) setViewing(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="salary-view-dialog">
          <DialogHeader><DialogTitle>Salary details · {viewing?.employee_name}</DialogTitle></DialogHeader>
          {viewing && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-1" data-testid="salary-view-body">
              <VRow label="Employee">{viewing.employee_name || "—"}</VRow>
              <VRow label="Business unit">{viewing.unit_name || "—"}</VRow>
              <VRow label="Category">{viewing.staff_category || "—"}{isContractor(viewing.staff_category) ? " (Subcontractor)" : ""}</VRow>
              <VRow label="Payment schedule">{viewing.payment_schedule || "—"}</VRow>
              {isContractor(viewing.staff_category) ? (
                <>
                  <VRow label="Contract amount"><span className="text-purple-700">{fmtFCFA(viewing.contract_amount || 0)}</span></VRow>
                  <VRow label="Advance"><span className="text-amber-600">{fmtFCFA(viewing.advance || 0)}</span></VRow>
                  <VRow label="Balance"><span className="text-emerald-700">{fmtFCFA((viewing.contract_amount || 0) - (viewing.advance || 0))}</span></VRow>
                </>
              ) : (
                <VRow label="Salary">{fmtFCFA(viewing.salary || 0)}</VRow>
              )}
              {(viewing.allowances || []).filter((a) => (a.amount || 0) > 0).map((a) => (
                <VRow key={a.key} label={`${LABELS[a.key] || a.key} allowance`}>{fmtFCFA(a.amount)} · {a.interval}</VRow>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewing(null)} data-testid="salary-view-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payrollOpen} onOpenChange={setPayrollOpen}>
        <DialogContent className="max-w-md" data-testid="payroll-filter-dialog">
          <DialogHeader><DialogTitle>Payroll summary</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <F label="Business unit">
              <Select value={pUnit} onValueChange={setPUnit}>
                <SelectTrigger data-testid="payroll-unit-select"><SelectValue placeholder="All units" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All units</SelectItem>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label="From date"><Input type="date" value={pFrom} onChange={(e) => setPFrom(e.target.value)} data-testid="payroll-from" /></F>
              <F label="To date"><Input type="date" value={pTo} onChange={(e) => setPTo(e.target.value)} data-testid="payroll-to" /></F>
            </div>
            <p className="text-xs text-slate-400">Leave blank for all. Dates filter by when each salary record was created.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayrollOpen(false)}>Cancel</Button>
            <Button data-testid="payroll-download" onClick={downloadPayroll} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><FileText className="h-4 w-4 mr-2" /> Download PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const VRow = ({ label, children }) => (
  <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-sm">
    <span className="text-xs uppercase tracking-wide text-slate-400">{label}</span>
    <span className="font-medium text-slate-800 text-right">{children}</span>
  </div>
);
